import documentsApi from "@wkt/api/documents/documentsAPI";
import { userApi } from "@wkt/api/user";
import { getAccessToken, getKioskEmployeeId } from "@wkt/lib/auth-context";
import { useSessionCatalogStore } from "@wkt/stores/sessionCatalog";
import { useShellStore } from "@wkt/stores/shell";

export const BADGE_TYPES = {
  NOTIFICATIONS: 0,
  NEWS: 1,
  CHECKIN: 2,
  PROCEDURES: 3,
  DOCUMENTS: 4,
} as const;

function pendingDocumentCount(documentsInfo: {
  documents?: Array<{
    documentPath?: string | null;
    documentType?: number | null;
    requestStatus?: number | null;
  }>;
  pendingReviews?: unknown[] | null;
}): number {
  const documents = Array.isArray(documentsInfo.documents)
    ? documentsInfo.documents
    : [];

  const pendingDocuments = documents.filter((document) => {
    if (document.documentPath != null) {
      return false;
    }

    return (
      document.documentType === 2 ||
      document.documentType === 3 ||
      (document.documentType === 5 && document.requestStatus === 1)
    );
  }).length;

  const pendingReviews = Array.isArray(documentsInfo.pendingReviews)
    ? documentsInfo.pendingReviews.length
    : 0;

  return pendingDocuments + pendingReviews;
}

export function useBadges() {
  const sessionCatalogStore = useSessionCatalogStore();
  const shellStore = useShellStore();

  function computeFromEmployee(): void {
    const employee = sessionCatalogStore.employee;
    if (!employee) {
      shellStore.setBadges({
        home: 0,
        news: 0,
        checkin: 0,
        procedures: 0,
        documents: 0,
      });
      return;
    }

    const notifications = employee.notificationBadge ?? 0;
    const news = employee.newsBadge ?? 0;
    shellStore.setBadges({
      home: notifications + news,
      news,
      checkin: employee.checkinBadge ?? 0,
      procedures: employee.proceduresBadge ?? 0,
      documents: employee.documentsBadge ?? shellStore.badges.documents ?? 0,
    });
  }

  function refreshDocumentsBadge(): Promise<number> {
    if (!getAccessToken() && !getKioskEmployeeId()) {
      return Promise.resolve(shellStore.badges.documents ?? 0);
    }
    return new Promise((resolve) => {
      documentsApi.getDocumentsInfo(
        (documentsInfo) => {
          const nextCount = pendingDocumentCount(documentsInfo ?? {});
          shellStore.setBadge("documents", nextCount);
          resolve(nextCount);
        },
        () => {
          resolve(shellStore.badges.documents ?? 0);
        },
      );
    });
  }

  function refreshBadges(): Promise<void> {
    return new Promise((resolve) => {
      userApi.getEmployeeWithKioskCheck(
        async (employee) => {
          sessionCatalogStore.employee = employee ?? null;
          if (sessionCatalogStore.sessionInformation) {
            sessionCatalogStore.sessionInformation.employee = employee;
          }
          computeFromEmployee();
          await refreshDocumentsBadge();
          resolve();
        },
        async () => {
          computeFromEmployee();
          await refreshDocumentsBadge();
          resolve();
        },
      );
    });
  }

  function clearBadge(type: number): Promise<void> {
    const employee = sessionCatalogStore.employee;
    if (!employee) {
      return Promise.resolve();
    }

    let currentCount = 0;

    switch (type) {
      case BADGE_TYPES.NOTIFICATIONS:
        currentCount = employee.notificationBadge ?? 0;
        if (currentCount <= 0) {
          return Promise.resolve();
        }
        employee.notificationBadge = 0;
        break;
      case BADGE_TYPES.NEWS:
        currentCount = employee.newsBadge ?? 0;
        if (currentCount <= 0) {
          return Promise.resolve();
        }
        employee.newsBadge = 0;
        break;
      case BADGE_TYPES.CHECKIN:
        currentCount = employee.checkinBadge ?? 0;
        if (currentCount <= 0) {
          return Promise.resolve();
        }
        employee.checkinBadge = 0;
        break;
      case BADGE_TYPES.PROCEDURES:
        currentCount = employee.proceduresBadge ?? 0;
        if (currentCount <= 0) {
          return Promise.resolve();
        }
        employee.proceduresBadge = 0;
        break;
      case BADGE_TYPES.DOCUMENTS:
        currentCount = employee.documentsBadge ?? 0;
        if (currentCount <= 0 && (shellStore.badges.documents ?? 0) <= 0) {
          return Promise.resolve();
        }
        employee.documentsBadge = 0;
        shellStore.setBadge("documents", 0);
        break;
      default:
        return Promise.resolve();
    }

    computeFromEmployee();

    return new Promise((resolve) => {
      userApi.clearEmployeeBadge(
        type,
        () => resolve(),
        () => resolve(),
      );
    });
  }

  return {
    clearBadge,
    computeFromEmployee,
    refreshBadges,
    refreshDocumentsBadge,
  };
}
